import { fetchAPI } from "./utils";

// Types moved from the original stenomatic.ts file
export interface StenomaticClient {
    id: string;
    name: string;
    parent_customer_id: string;
}

// New types for room-folder linking
export interface StenomaticRoomLink {
    id: string;
    externalRoomId: string;
    folderId: string;
    folder?: {
        id: string;
        internalName: string;
        companySingleTrackLocalized: Array<{
            language: string;
            name: string;
            description?: string;
        }>;
    };
}

export interface StenomaticRoom {
    id: string;
    conference_id: string;
    title: string;
    description: string;
    api_key: string;
    secret: string;
    client_branch_id: string;
    to_languages: string[];
    start_date: string;
    end_date: string;
    create_date: string;
    update_date: string;
    main_image_url: string;
    main_image_bucket_file_name: string;
    sponsorship_image_bucket_file_name: string;
    sponsorship_credit_title: string;
    sponsorship_title: string;
    sponsorship_description: string;
    sponsorship_info_url: string;
    sponsorship_image_url: string;
    closed_captions_endpoint: string;
    usage_in_seconds: {
        total: number;
        twenty_four_hour: number;
    };
    is_archived: boolean;
    passwords?: {
        speakers: string;
        listeners: string;
    };
    has_participant_password?: boolean;
    linkedFolders?: StenomaticRoomLink[];
}

export interface RoomCreateRequest {
    id?: string;
    title: string;
    description: string;
    to_languages: string[];
    sponsorship_credit_title?: string;
    sponsorship_title?: string;
    sponsorship_description?: string;
    sponsorship_info_url?: string;
    passwords?: {
        speaker: string;
        participant: string;
    };
    folderIds?: string[];
}

export interface RoomUpdateRequest {
    title?: string;
    description?: string;
    to_languages?: string[];
    sponsorship_credit_title?: string;
    sponsorship_title?: string;
    sponsorship_description?: string;
    sponsorship_info_url?: string;
    update_date: string; // Required field
    folderIds?: string[];
}

export interface ApiUsage {
    speech_recognition: number;
    voice_translate: number;
}

export interface RoomPasswords {
    speaker: string;
    participant: string;
}

export interface UsageData {
    speech_recognition?: number;
    voice_translate?: number;
    [key: string]: any;
}

export interface LinkRoomsToFolderRequest {
    roomIds: string[];
}

export interface UnlinkRoomsFromFolderRequest {
    roomIds: string[];
}

export interface LinkRoomsToFolderResponse {
    message: string;
    createdLinks: StenomaticRoomLink[];
    skippedRooms: string[];
    folder: any;
}

export interface UnlinkRoomsFromFolderResponse {
    message: string;
    unlinkedCount: number;
    folder: any;
}

export interface GetAvailableRoomsResponse {
    message: string;
    availableRooms: StenomaticRoom[];
    linkedRooms: StenomaticRoom[];
}

// API Response types
export interface CreateStenomaticClientResponse {
    message: string;
    stenomaticClientId: string;
    companyName: string;
    stenomaticClient: StenomaticClient;
}

export interface CreateStenomaticRoomResponse {
    message: string;
    room: StenomaticRoom;
    roomLinks?: StenomaticRoomLink[];
}

export interface GetCompanyStenomaticRoomsResponse {
    message: string;
    rooms: StenomaticRoom[];
    companyId: string;
    stenomaticClientId: string;
}

export interface GetStenomaticClientUsageResponse {
    message: string;
    companyId: string;
    stenomaticClientId: string;
    usage: UsageData;
}

// Stenomatic Backend API endpoints

export const createStenomaticClient = async (
    token: string,
    companyId: string,
): Promise<CreateStenomaticClientResponse> => {
    return fetchAPI<CreateStenomaticClientResponse>("/rooms/create-client", "POST", { companyId }, token);
};

export const createStenomaticRoom = async (
    token: string,
    roomData: {
        title: string;
        description: string;
        toLanguages: string[];
        speakerPassword?: string;
        participantPassword?: string;
        folderIds?: string[];
    },
): Promise<CreateStenomaticRoomResponse> => {
    return fetchAPI<CreateStenomaticRoomResponse>(
        "/rooms/create-room",
        "POST",
        {
            title: roomData.title,
            description: roomData.description,
            toLanguages: roomData.toLanguages,
            speakerPassword: roomData.speakerPassword,
            participantPassword: roomData.participantPassword,
            folderIds: roomData.folderIds,
        },
        token,
    );
};

export const getStenomaticClientUsage = async (
    token: string,
    params?: {
        fromTimestamp?: number;
        toTimestamp?: number;
    },
): Promise<GetStenomaticClientUsageResponse> => {
    return fetchAPI<GetStenomaticClientUsageResponse>("/rooms/get-client-usage", "POST", params || {}, token);
};

export const listStenomaticClients = async (token: string) => {
    return fetchAPI("/rooms/list-clients", "GET", undefined, token);
};

export const getCompanyStenomaticRooms = async (token: string): Promise<GetCompanyStenomaticRoomsResponse> => {
    return fetchAPI<GetCompanyStenomaticRoomsResponse>("/rooms/get-company-rooms", "GET", undefined, token);
};

// Note: These endpoints need to be implemented in your backend
export const updateStenomaticRoom = async (
    token: string,
    roomId: string,
    roomData: {
        title?: string;
        description?: string;
        toLanguages?: string[];
        speakerPassword?: string;
        participantPassword?: string;
        folderIds?: string[];
    },
) => {
    return fetchAPI(
        `/rooms/update-room/${roomId}`,
        "POST",
        {
            title: roomData.title,
            description: roomData.description,
            toLanguages: roomData.toLanguages,
            speakerPassword: roomData.speakerPassword,
            participantPassword: roomData.participantPassword,
            folderIds: roomData.folderIds,
        },
        token,
    );
};

export const deleteStenomaticRoom = async (token: string, roomId: string) => {
    return fetchAPI(`/rooms/update-room/${roomId}`, "POST", { isArchived: true }, token);
};

// New API functions for room-folder linking
export const linkRoomsToFolder = async (
    token: string,
    folderId: string,
    roomIds: string[],
): Promise<LinkRoomsToFolderResponse> => {
    return fetchAPI<LinkRoomsToFolderResponse>(`/folders/${folderId}/link-rooms`, "POST", { roomIds }, token);
};

export const unlinkRoomsFromFolder = async (
    token: string,
    folderId: string,
    roomIds: string[],
): Promise<UnlinkRoomsFromFolderResponse> => {
    return fetchAPI<UnlinkRoomsFromFolderResponse>(`/folders/${folderId}/unlink-rooms`, "DELETE", { roomIds }, token);
};

export const getAvailableRoomsForFolder = async (
    token: string,
    folderId?: string,
): Promise<GetAvailableRoomsResponse> => {
    const endpoint = folderId ? `/folders/${folderId}/available-rooms` : "/folders/available-rooms";
    return fetchAPI<GetAvailableRoomsResponse>(endpoint, "GET", undefined, token);
};

// Utility functions moved from the original stenomatic.ts file
export const formatUsageTime = (seconds: number): string => {
    if (seconds === 0) return "0s";

    const totalSeconds = Math.floor(seconds);
    const minutes = Math.floor(totalSeconds / 60);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    const remainingSeconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}h ${remainingMinutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
        return `${minutes}m ${remainingSeconds}s`;
    } else {
        return `${totalSeconds}s`;
    }
};

export const formatMSUsageTime = (nanoseconds: number): string => {
    if (nanoseconds === 0) return "0s";

    const seconds = Math.floor(nanoseconds / 1000000000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
        return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
        return `${minutes}m ${seconds % 60}s`;
    } else {
        return `${seconds}s`;
    }
};

export const generateRoomUrls = (roomId: string) => ({
    speakerUrl: `https://stm.live/${roomId}`,
    participantUrl: `https://hub.rewindstories.com/?room=${roomId}`,
});
